import { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Home, Plus, BarChart3, Settings, Layers, Menu } from 'lucide-react';
import { BottomNav } from './ui/BottomNav';
import { Drawer } from './components/Drawer';
import { Sidebar } from './components/Sidebar';
import { TOKENS } from './ui/tokens';
import type { LucideProps } from 'lucide-react';
import type { ComponentType } from 'react';

const TABS = [
  { key: '/', icon: Home as ComponentType<LucideProps>, label: 'Home' },
  { key: '/techniques', icon: Layers as ComponentType<LucideProps>, label: 'Techniques' },
  { key: '/session-log', icon: Plus as ComponentType<LucideProps>, label: 'Log' },
  { key: '/stats', icon: BarChart3 as ComponentType<LucideProps>, label: 'Stats' },
  { key: '/settings', icon: Settings as ComponentType<LucideProps>, label: 'More' },
] as const;

/** Match active tab by longest prefix */
function useActiveTab() {
  const location = useLocation();
  return TABS
    .map(t => t.key)
    .filter(key => location.pathname.startsWith(key))
    .sort((a, b) => b.length - a.length)[0] ?? '/';
}

/**
 * useMediaQuery — returns true when the CSS media query matches.
 * Uses window.matchMedia with a listener so it live-updates on resize.
 */
function useMediaQuery(query: string): boolean {
  const [match, setMatch] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });
  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatch(e.matches);
    // Sync in case it changed between render and effect
    setMatch(mql.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);
  return match;
}

const DESKTOP_BP = '(min-width: 1024px)';

export function AppShell() {
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isDesktop = useMediaQuery(DESKTOP_BP);
  const activeTab = useActiveTab();

  return (
    <div style={{
      display: 'flex',
      height: '100dvh',
      background: TOKENS.bg,
      fontFamily: "system-ui, -apple-system, sans-serif",
      overflow: 'hidden',
    }}>
      {/* Desktop sidebar — ≥1024px only */}
      {isDesktop && <Sidebar />}

      {/* Center column: header + content + (mobile) bottom nav */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,          /* prevent flex child overflow */
        maxWidth: isDesktop ? 768 : 390,
        margin: isDesktop ? '0 auto' : '0',
        width: '100%',
      }}>
        {/* Top bar — hamburger only on mobile; desktop uses sidebar */}
        <header style={{
          display: 'flex',
          alignItems: 'center',
          padding: '8px 12px',
          background: TOKENS.bg,
          borderBottom: `1px solid ${TOKENS.border}`,
          position: 'sticky',
          top: 0,
          zIndex: 30,
        }}>
          {!isDesktop && (
            <button
              type="button"
              aria-label="Open menu"
              aria-haspopup="dialog"
              aria-expanded={drawerOpen}
              onClick={() => setDrawerOpen(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 38,
                height: 38,
                borderRadius: 8,
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                color: TOKENS.textPrimary,
                transition: 'background-color 120ms ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = TOKENS.field; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <Menu size={22} strokeWidth={2} />
            </button>
          )}
        </header>

        <main style={{ flex: 1, overflowY: 'auto' }}>
          <Outlet />
        </main>

        {/* Bottom nav — mobile only */}
        {!isDesktop && (
          <BottomNav
            tabs={TABS.map(t => ({ ...t }))}
            active={activeTab}
            onChange={(key) => navigate(key)}
          />
        )}
      </div>

      {/* Mobile drawer — hidden on desktop */}
      {!isDesktop && (
        <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      )}
    </div>
  );
}
